import { UsageUnitType, Duration } from 'rpc/model/squareup/sub2/data';
import { Currency } from 'rpc/model/squareup/connect/v2/common/money';
import { toCurrencyWithMinorUnitAmount } from 'utilities/currency';
import { SoftwareProductGroupType } from 'rpc/model/squareup/solidshop/v2/service/products/list_software_product_groups';

import { ReactComponent as AppointmentsIcon } from 'images/icons/software/appointments-icon.svg';
import { ReactComponent as OnlineIcon } from 'images/icons/software/online-icon.svg';
import { ReactComponent as RestaurantsIcon } from 'images/icons/software/restaurants-icon.svg';
import { ReactComponent as RetailIcon } from 'images/icons/software/retail-icon.svg';
import { ReactComponent as PosIcon } from 'images/icons/software/pos-icon.svg';
import { Money } from 'rpc/model/squareup/common/currency';
import { CurrencyCode } from 'rpc/model/squareup/common/currency_codes';
import { SOFTWARE_BASE_PRODUCT_KEYS } from 'constants/software';
import { BASE_PRODUCT_KEYS } from 'constants/products';

// Formats a minor-unit amount as a whole-dollar currency string (e.g. "$49"
// instead of "$49.00"). Used for software pricing display.
const formatWholePrice = (minorUnitAmount, currencyCode) =>
  toCurrencyWithMinorUnitAmount(minorUnitAmount, currencyCode)?.replace(
    /[.,]00(?!\d)/,
    '',
  );

/**
 * Sums the kiosk hardware quantity from a cart summary, defaulting to 1 when
 * the cart has no kiosk line items so callers can multiply per-device pricing
 * without special-casing the empty case.
 *
 * @param {Array} cartSummary - line items from order/cart summary responses
 * @returns {number} kiosk device count (>= 1)
 */
export const getKioskDeviceCount = (cartSummary = []) =>
  cartSummary
    .filter((item) => item?.base_product_key === BASE_PRODUCT_KEYS.S3B_KIOSK)
    .reduce((sum, item) => sum + (item?.quantity || 0), 0) || 1;

/**
 * Bundles the three values that drive kiosk subscription pricing on the cart
 * and order summary surfaces — `is_pnp_subscribed` and `active_location_count`
 * from the current user, plus the cart's kiosk device count. Each of these is
 * a small read on its own, but the surfaces consume them together, so a single
 * helper keeps the call sites uniform.
 *
 * @param {Object} orderData - order/order-summary response (must expose
 *   `cart_summary` and `current_user`)
 * @returns {{ isPnpSubscribed: boolean, locationCount: number, kioskDeviceCount: number }}
 */
export const getKioskPricingContext = (orderData) => ({
  isPnpSubscribed: Boolean(orderData?.current_user?.is_pnp_subscribed),
  locationCount: orderData?.current_user?.active_location_count || 1,
  kioskDeviceCount: getKioskDeviceCount(orderData?.cart_summary),
});

// this should match the subscription states in Solidshop::Software::SoftwareSubscriptionStates
export const SOFTWARE_SUBSCRIPTION_STATES = Object.freeze({
  ACTIVE_SUBSCRIPTION: 'SOFTWARE_ACTIVE_SUBSCRIPTION',
  FREE_TRIAL_EXPIRED: 'SOFTWARE_FREE_TRIAL_EXPIRED',
  FREE_TRIAL_ACTIVE: 'SOFTWARE_FREE_TRIAL_ACTIVE',
  NOT_SUBSCRIBED: 'SOFTWARE_NOT_SUBSCRIBED',
  SUBSCRIPTION_PAUSED: 'SOFTWARE_SUBSCRIPTION_PAUSED',
  SUBSCRIPTION_PRESALE: 'SOFTWARE_SUBSCRIPTION_PRESALE',
  SUBSCRIPTION_WITH_ACTIVE_SUPERSET:
    'SOFTWARE_SUBSCRIPTION_WITH_ACTIVE_SUPERSET',
});

/**
 * Returns the UsageBucket that should drive cost + unit_type rendering for a
 * multi_bucket_pricing plan. Prefers the FNB_KIOSK_DEVICE bucket when present
 * (kiosk P&P plans surface the device fee even when it isn't the first bucket);
 * otherwise falls back to the first bucket.
 * @param {squareup.sub2.data.MultiBucketPricing as JSON} multiBucketPricing
 * @returns {squareup.sub2.data.UsageBucket as JSON | undefined}
 */
const findPrimaryMultiBucket = (multiBucketPricing) => {
  const buckets = multiBucketPricing?.buckets || [];
  return (
    buckets.find((bucket) => bucket?.unit_type === 'FNB_KIOSK_DEVICE') ||
    buckets[0]
  );
};

const isKioskBucket = (bucket) => bucket?.unit_type === 'FNB_KIOSK_DEVICE';

/**
 * Returns the pricing bucket that represents the base (Square Plus) portion of
 * a multi_bucket_pricing kiosk plan. We prefer BILLABLE_LOCATION since that's
 * the bucket SaaS uses for the Plus/Premium base fee.
 * @param {squareup.sub2.data.MultiBucketPricing as JSON} multiBucketPricing
 * @returns {squareup.sub2.data.UsageBucket as JSON | undefined}
 */
const findBaseMultiBucket = (multiBucketPricing) => {
  const buckets = multiBucketPricing?.buckets || [];
  return buckets.find((bucket) => bucket?.unit_type === 'BILLABLE_LOCATION');
};

/**
 * Get the cost (squareup.common.Money) of the Sub2 plan
 * @param {squareup.solidshop.v2.resources.Plan as JSON} plan
 * @returns {squareup.common.Money as JSON} money
 */
export const getPlanCost = (plan) => {
  const { bucket_pricing, fixed_pricing, multi_bucket_pricing } =
    plan?.pricing_model || {};

  const primaryBucket = findPrimaryMultiBucket(multi_bucket_pricing);
  const kioskBucketCost = isKioskBucket(primaryBucket)
    ? primaryBucket.cost
    : null;

  return (
    bucket_pricing?.cost ||
    fixed_pricing?.cost ||
    kioskBucketCost ||
    multi_bucket_pricing?.baseCost ||
    primaryBucket?.cost ||
    null
  );
};

/**
 * @param {*} plan
 * @returns {CurrencyCode} currency code
 */
export const getPlanCurrency = (plan) => {
  const { bucket_pricing, fixed_pricing, multi_bucket_pricing } =
    plan?.pricing_model || {};

  const primaryBucket = findPrimaryMultiBucket(multi_bucket_pricing);

  return (
    bucket_pricing?.cost?.currency_code ||
    fixed_pricing?.cost?.currency_code ||
    multi_bucket_pricing?.baseCost?.currency_code ||
    primaryBucket?.cost?.currency_code ||
    CurrencyCode.USD
  );
};

/**
 * Multiply a cost proto by a scalar, returning a new cost object.
 * @param {squareup.common.Money as JSON} cost
 * @param {number} multiplier
 * @returns {squareup.common.Money as JSON}
 */
const multiplyCost = (cost, multiplier) => {
  if (!cost || multiplier <= 1) return cost;
  return { ...cost, amount: cost.amount * multiplier };
};

/**
 * Break a plan into the individual priced rows we want to surface.
 * For multi_bucket_pricing kiosk plans we return both the base (BILLABLE_LOCATION)
 * and kiosk (FNB_KIOSK_DEVICE) buckets, unless `isPnpSubscribed` is true, in which
 * case the base line is suppressed (merchant already pays the Plus fee elsewhere).
 * For fixed_pricing / bucket_pricing / single-bucket plans we return a single item
 * matching the existing getPlanCost behavior.
 *
 * When `locationCount` or `kioskDeviceCount` are provided, the corresponding
 * bucket costs are multiplied to reflect the total estimated subscription price.
 *
 * The base + device split is driven purely by the buckets present on the plan.
 * The backend only attaches the FNB_KIOSK_DEVICE bucket to lines where the
 * per-device fee applies (the kiosk product), so bundle lines (sw-square-*)
 * arrive with the BILLABLE_LOCATION bucket only and render as a single line.
 *
 * @param {squareup.solidshop.v2.resources.Plan as JSON} plan
 * @param {Object} [options]
 * @param {boolean} [options.isPnpSubscribed] - whether the user already has an
 *   active Square Plus/Premium (P&P) subscription
 * @param {number} [options.locationCount] - number of billable locations (from MDS)
 * @param {number} [options.kioskDeviceCount] - number of kiosk hardware devices in the cart
 * @returns {Array<{ cost: squareup.common.Money as JSON, unitType: string | null, isBase: boolean }>}
 */
export const getSoftwarePlanLineItems = (
  plan,
  { isPnpSubscribed = false, locationCount = 1, kioskDeviceCount = 1 } = {},
) => {
  const { multi_bucket_pricing } = plan?.pricing_model || {};

  const baseBucket = findBaseMultiBucket(multi_bucket_pricing);
  const primaryBucket = findPrimaryMultiBucket(multi_bucket_pricing);

  // The presence of an FNB_KIOSK_DEVICE bucket is authoritative: the BE only
  // attaches it to lines where the per-device fee actually applies (kiosk).
  // Bundle lines (sw-square-*) arrive with the BILLABLE_LOCATION bucket only,
  // so they fall through to the single-line path below.
  if (
    baseBucket &&
    isKioskBucket(primaryBucket) &&
    baseBucket !== primaryBucket
  ) {
    const lineItems = [
      {
        cost: multiplyCost(primaryBucket.cost, kioskDeviceCount),
        unitType: primaryBucket.unit_type,
        isBase: false,
      },
    ];

    if (!isPnpSubscribed) {
      lineItems.unshift({
        cost: multiplyCost(baseBucket.cost, locationCount),
        unitType: baseBucket.unit_type,
        isBase: true,
      });
    }

    return lineItems;
  }

  const cost = getPlanCost(plan);
  if (!cost) return [];

  return [
    {
      cost,
      unitType:
        plan?.pricing_model?.bucket_pricing?.unit_type ||
        primaryBucket?.unit_type ||
        null,
      isBase: false,
    },
  ];
};

/**
 * Computes the total cost across all visible line items for a single plan.
 * Shared by getSoftwarePriceLabel, getCombinedPlanPrice, and sumSoftwarePricing
 * so the sum-and-extract logic lives in one place.
 *
 * @param {squareup.solidshop.v2.resources.Plan as JSON} plan
 * @param {Object} [options]
 * @param {boolean} [options.isPnpSubscribed]
 * @param {number} [options.locationCount]
 * @param {number} [options.kioskDeviceCount]
 * @returns {{ amount: number, currencyCode: string } | null}
 */
const computeTotalSoftwareCost = (plan, opts = {}) => {
  const lineItems = getSoftwarePlanLineItems(plan, opts);
  if (lineItems.length === 0) return null;

  const totalAmount = lineItems.reduce(
    (sum, item) => sum + (item.cost?.amount || 0),
    0,
  );
  const currencyCode = lineItems[0].cost?.currency_code;
  if (currencyCode === null || currencyCode === undefined) return null;

  return { amount: totalAmount, currencyCode };
};

/**
 * Sum the prices from all software plans, matching the rendered line items.
 * Kiosk P&P plans produce two priced lines (base + device) unless the merchant
 * already has an active Plus/Premium subscription.
 *
 * @param {Array[squareup.solidshop.v2.resources.Plan as JSON]} softwarePlans
 * @param {Object} [options]
 * @param {boolean} [options.isPnpSubscribed]
 * @param {number} [options.locationCount]
 * @param {number} [options.kioskDeviceCount]
 * @returns {squareup.common.Money} money
 */
export const sumSoftwarePricing = (softwarePlans = [], opts = {}) => {
  const summedAmount = softwarePlans.reduce((acc, plan) => {
    const result = computeTotalSoftwareCost(plan, opts);
    return acc + (result?.amount || 0);
  }, 0);

  // getPlanCurrency may return a numeric ordinal (e.g. 826) or a string key
  // (e.g. "GBP") depending on how the proto was deserialized. Normalize to the
  // string key so downstream consumers can pass it directly to Dinero/Intl.
  const rawCurrency = getPlanCurrency(softwarePlans[0]);
  const resolvedCurrency =
    typeof rawCurrency === 'number' ? CurrencyCode[rawCurrency] : rawCurrency;

  return Money.create({
    amount: summedAmount,
    currencyCode: resolvedCurrency,
  });
};

/**
 * Sort cart summary products by software or hardware
 * @param {Array} products
 * @returns {Hash} sorted products
 */
export const sortProductByHardwareOrSoftware = (products = []) => {
  const sortedProducts = {
    software: [],
    hardware: [],
  };

  products.forEach((product) => {
    if (product.is_software) {
      sortedProducts.software.push(product);
    } else {
      sortedProducts.hardware.push(product);
    }
  });

  return sortedProducts;
};

/**
 * Returns the icon based on the mapped product group type.
 * @param groupType software group type of software
 * @returns svg react icon
 */
export const getSoftwareTypeIcon = (groupType) => {
  const type = SoftwareProductGroupType[groupType];

  switch (type) {
    case SoftwareProductGroupType.APPOINTMENTS: {
      return AppointmentsIcon;
    }
    case SoftwareProductGroupType.ONLINE: {
      return OnlineIcon;
    }
    case SoftwareProductGroupType.RESTAURANTS: {
      return RestaurantsIcon;
    }
    case SoftwareProductGroupType.RETAIL: {
      return RetailIcon;
    }
    case SoftwareProductGroupType.POS: {
      return PosIcon;
    }
    default: {
      return null;
    }
  }
};

/**
 * Returns a localized string for the software price
 * @param plan
 * @returns localized string or null
 */
export const getPlanPrice = (plan) => {
  const moneyModel = getPlanCost(plan);

  return moneyModel
    ? formatWholePrice(moneyModel.amount, Currency[moneyModel.currencyCode])
    : null;
};

/**
 * Returns the duration string based on tense.
 * @param t translations t
 * @param durationUnit the duration unit
 * @param isSingular whether the tense is singale or not singular (plural)
 * @returns translation for duration unit
 */
export const getDurationUnitText = (t, durationUnit, isSingular = true) => {
  const baseKey = `cart.software.duration.durationUnits.${durationUnit
    .toString()
    .toLowerCase()}`;

  const type = Duration.DurationUnit[durationUnit];
  switch (type) {
    case Duration.DurationUnit.DAY:
    case Duration.DurationUnit.MONTH:
    case Duration.DurationUnit.YEAR: {
      return t(`${baseKey}.${isSingular ? 'singular' : 'plural'}`);
    }
    default: {
      return null;
    }
  }
};

/**
 * Return a string describing the renewal cycle.
 * @param t translations t
 * @param duration the duration
 * @returns a translated renewal cycle string
 */
export const buildRenewalCycle = (t, duration) => {
  if (!duration.count || !duration.unit) return null;

  const isSingleUnit = duration.count === 1;
  const unit = getDurationUnitText(t, duration.unit, isSingleUnit);

  if (!unit) return null;

  return isSingleUnit
    ? `/${unit}`
    : ` ${t('cart.software.duration.everyXUnits', {
        count: duration.count,
        unit,
      })}`;
};

/**
 * Returns the pricing unit messaging based on pricing unit type.
 * @param t translations t
 * @param usageUnitType UsageUnitType
 * @returns translation for usage unit
 */
export const getPricingUnitTypeText = (t, usageUnitType) => {
  const type = UsageUnitType[usageUnitType];
  switch (type) {
    case UsageUnitType.APPOINTMENTS_LOCATIONS:
    case UsageUnitType.RETAIL_LOCATION:
    case UsageUnitType.RESTAURANTS_LOCATION:
    case UsageUnitType.RESTAURANTS_MOBILE_LOCATION:
    case UsageUnitType.RESTAURANTS_ADDITIONAL_DEVICE:
    case UsageUnitType.FNB_KIOSK_DEVICE: {
      return t(`cart.software.usageUnitTypes.${usageUnitType.toLowerCase()}`);
    }
    default: {
      return null;
    }
  }
};

/**
 *  Returns a string describing the trial period.
 * @param t translations t
 * @param duration the duration
 * @returns a translated strial period string
 */
export const buildTrialDuration = (t, duration) => {
  const count = duration?.count;
  const unit = duration?.unit;

  if (!count || !unit) return null;

  // The text should always be in the singular
  // Ex: 30-day and not 30-days
  const unitString = getDurationUnitText(t, unit, true);

  return t('cart.software.duration.freeTrial', {
    count,
    unit: unitString,
  });
};

export const buildPricingLabel = (t, plan) => {
  // The Sub2 Pricing proto uses oneof multiple product pricing strctures, so we must check which
  // is populated, then build the pricing string.
  // https://prototype.sqprod.co/#/docs/squareup.solidshop.v2.resources.Plan#pricing_model
  // Ex: 30-day free trial, then $89/month per location

  if (!plan) return null;
  if (!plan?.renewal_cycle) return null;

  let pricing = '';
  const renewalUnit = buildRenewalCycle(t, plan.renewal_cycle);

  if (!renewalUnit) return null;

  const price = getPlanPrice(plan);

  if (plan.pricing_model?.bucket_pricing) {
    if (!plan.pricing_model?.bucket_pricing.unit_type) return null;

    if (!price) return null;

    const unitText = getPricingUnitTypeText(
      t,
      plan.pricing_model?.bucket_pricing.unit_type,
    );

    if (!unitText) return null;

    pricing = t('cart.software.label.pricing', {
      price,
      renewalUnit,
      unitText,
    });
  } else if (plan.pricing_model?.fixed_pricing) {
    if (!price) return null;

    pricing = `${price}${renewalUnit}`;
  } else if (plan.pricing_model?.multi_bucket_pricing) {
    const primaryBucket = findPrimaryMultiBucket(
      plan.pricing_model?.multi_bucket_pricing,
    );
    if (!primaryBucket?.unit_type) return null;

    if (!price) return null;

    const unitText = getPricingUnitTypeText(t, primaryBucket.unit_type);
    if (!unitText) return null;

    pricing = t('cart.software.label.pricing', {
      price,
      renewalUnit,
      unitText,
    });
  } else {
    // No pricing model exist, so use label
    // pricing_model.tieredPricing not implemented
    pricing = plan.pricing_model?.label || '';
  }

  const trialMessage = plan.free_trial_duration
    ? buildTrialDuration(t, plan.free_trial_duration)
    : null;

  if (!trialMessage) return pricing;

  return t('cart.software.label.pricingOptions', {
    trialMessage,
    pricing,
  });
};

/**
 * Returns the price per renewal cycle for a software product.
 *
 * For multi-line kiosk plans (P&P Plus/Premium with both BILLABLE_LOCATION
 * and FNB_KIOSK_DEVICE buckets, surfaced when `saas-shop-kiosk-improvements`
 * is on) this sums every visible line item so the cart row matches the
 * Subscriptions subtotal.
 *
 * For everything else — legacy `fnb-kiosk`, fixed_pricing, bucket_pricing,
 * single-bucket multi_bucket, all non-kiosk software — we delegate to the
 * pre-existing `getPlanPrice` path so behavior with the flag off is byte-for-
 * byte unchanged.
 *
 * @param {Object} options
 * @param {CartSummaryPlan} options.plan - software plan
 * @param {TFunction} options.t - i18n t function
 * @param {boolean} [options.isPnpSubscribed] - whether the user already has an
 *   active Square Plus / Premium subscription (suppresses the base line)
 * @param {number} [options.locationCount] - number of billable locations
 * @param {number} [options.kioskDeviceCount] - number of kiosk hardware devices in cart
 * @returns {String} softwarePrice - price per renewal cycle in the form of $69/month
 */
export const getSoftwarePriceLabel = ({
  plan,
  t,
  isPnpSubscribed = false,
  locationCount = 1,
  kioskDeviceCount = 1,
}) => {
  if (!plan) return null;

  const renewalCycle = buildRenewalCycle(t, plan.renewal_cycle);
  if (!renewalCycle) return null;

  const result = computeTotalSoftwareCost(plan, {
    isPnpSubscribed,
    locationCount,
    kioskDeviceCount,
  });
  if (!result) return null;

  const planPriceLabel = formatWholePrice(result.amount, result.currencyCode);
  return planPriceLabel ? `${planPriceLabel}${renewalCycle}` : null;
};

/**
 * Returns the combined price across all visible line items for a plan.
 * For multi-bucket kiosk P&P plans this sums the base + device buckets so the
 * cart item price reflects the true total. For everything else, delegates to
 * `getPlanPrice` so behavior is unchanged.
 *
 * @param {squareup.solidshop.v2.resources.Plan as JSON} plan
 * @param {Object} [options]
 * @param {boolean} [options.isPnpSubscribed]
 * @param {number} [options.locationCount]
 * @param {number} [options.kioskDeviceCount]
 * @returns {String|null} formatted price (e.g. "$99.00")
 */
export const getCombinedPlanPrice = (plan, opts = {}) => {
  const result = computeTotalSoftwareCost(plan, opts);
  if (!result) return getPlanPrice(plan);

  return formatWholePrice(result.amount, result.currencyCode);
};

/**
 * For multi-bucket kiosk P&P plans, returns per-line breakdown info used to
 * render separate pricing rows in the cart and receipt sidebar.
 *
 * The per-line labels surface the *unit* price (e.g. "$50/mo per device") so
 * they remain stable as the merchant changes their device or location count.
 * Callers compute the multiplied total separately via `getCombinedPlanPrice`
 * / `sumSoftwarePricing`.
 *
 * Returns `null` for single-line plans (non-kiosk, FF off, or P&P subscriber)
 * so callers can fall back to the existing single-line rendering. Only the
 * kiosk plan carries both buckets — the BE strips the FNB_KIOSK_DEVICE bucket
 * from bundle plans — so this naturally returns null for bundle lines.
 *
 * @param {Object} options
 * @param {squareup.solidshop.v2.resources.Plan as JSON} options.plan
 * @param {TFunction} options.t
 * @param {boolean} [options.isPnpSubscribed]
 * @returns {Array<{ name: string, cartLabel: string, receiptLabel: string }> | null}
 */
export const getKioskPnpBreakdown = ({ plan, t, isPnpSubscribed = false }) => {
  if (!plan) return null;

  const lineItems = getSoftwarePlanLineItems(plan, { isPnpSubscribed });
  if (lineItems.length <= 1) return null;

  const renewalCycle = buildRenewalCycle(t, plan.renewal_cycle);
  if (!renewalCycle) return null;

  const formatCost = (cost) =>
    formatWholePrice(cost.amount, cost.currency_code);

  return lineItems.map((item) => {
    const price = formatCost(item.cost);
    if (item.isBase) {
      return {
        name: t('cart.software.kioskPnp.baseName'),
        cartLabel: t('cart.software.kioskPnp.basePriceBreakdown', {
          price,
          renewalUnit: renewalCycle,
        }),
        receiptLabel: t('cart.software.kioskPnp.basePriceLabel', {
          price,
          renewalUnit: renewalCycle,
        }),
      };
    }
    return {
      name: t('cart.software.kioskPnp.deviceName'),
      cartLabel: t('cart.software.kioskPnp.devicePriceBreakdown', {
        price,
        renewalUnit: renewalCycle,
      }),
      receiptLabel: t('cart.software.kioskPnp.devicePriceLabel', {
        price,
        renewalUnit: renewalCycle,
      }),
    };
  });
};

/**
 * Checks if the cart contains software with free hardware
 * used to block checkout if true
 * @param {Object} orderData - order data from solidshop
 * @returns {Boolean} isSoftwareWithFreeHardware
 */
export const isSoftwareWithFreeHardware = (orderData) => {
  if (!orderData) return false;

  const { software } = sortProductByHardwareOrSoftware(orderData.cart_summary);

  return Number(orderData.total.amount) === 0 && software.length > 0;
};

/**
 * Fetches the summary for a single software product
 * @param {Array} orderData - order data from solidshop
 * @param {Object} softwareProduct - software product
 * @returns {Object} summary - software summary parsed from order data and software summary
 */
export const fetchSoftwareSummary = (orderData, softwareProduct) => {
  if (!orderData) return null;

  const { software_summary: softwareSummary } = orderData;
  const summary = softwareSummary[softwareProduct.product_key];
  if (!summary) return null;

  return {
    baseProductKey: softwareProduct.base_product_key,
    softwareName: softwareProduct.product_variant.title,
    subscriptionState: summary.subscription_state,
  };
};

/**
 * Fetches the subscription states for the software products in the cart
 * @param {Array} orderData - order data from solidshop
 * @returns {Array} softwareSummaries - array of software subscription states
 */
export const fetchSoftwareSummaries = (orderData) => {
  if (!orderData) return [];

  const { cart_summary: cartSummary } = orderData;
  const { software } = sortProductByHardwareOrSoftware(cartSummary);

  return software
    .map((product) => {
      return fetchSoftwareSummary(orderData, product);
    })
    .filter(Boolean);
};

/**
 * Checks if any software in the cart has subscription states that would block checkout.
 * Kiosk products are exempt when the user has a P&P (Plus/Premium) subscription
 * because there is still a per-device fee, so the purchase is valid.
 * @param {Array} orderData - order data from solidshop
 * @returns {Boolean} anySoftwareBlockingCheckout
 */
export const anySoftwareBlockingCheckout = (orderData) => {
  if (!orderData) return false;

  const softwareSummaries = fetchSoftwareSummaries(orderData);
  if (softwareSummaries.length === 0) return false;

  const { isPnpSubscribed } = getKioskPricingContext(orderData);

  const blockingStates = new Set([
    SOFTWARE_SUBSCRIPTION_STATES.ACTIVE_SUBSCRIPTION,
    SOFTWARE_SUBSCRIPTION_STATES.SUBSCRIPTION_PAUSED,
    SOFTWARE_SUBSCRIPTION_STATES.SUBSCRIPTION_PRESALE,
    SOFTWARE_SUBSCRIPTION_STATES.SUBSCRIPTION_WITH_ACTIVE_SUPERSET,
  ]);

  return softwareSummaries.some((summary) => {
    if (
      isPnpSubscribed &&
      summary.baseProductKey === SOFTWARE_BASE_PRODUCT_KEYS.SW_KIOSK
    ) {
      return false;
    }
    return blockingStates.has(summary.subscriptionState);
  });
};

/**
 * when redirected from a login, check if any software require displaying messaging to the user
 * only allow redirect if all software in cart has a state of not subscribed.
 * Kiosk products are exempt when the user has a P&P subscription (per-device fee applies).
 * @param {Array} orderData - order data from solidshop
 * @returns {Boolean} anySoftwareBlockingCheckoutLoginRedirect
 */
export const anySoftwareBlockingCheckoutLoginRedirect = (orderData) => {
  if (!orderData) return false;

  const softwareSummaries = fetchSoftwareSummaries(orderData);
  if (softwareSummaries.length === 0) return false;

  const { isPnpSubscribed } = getKioskPricingContext(orderData);

  return softwareSummaries.some((summary) => {
    if (
      isPnpSubscribed &&
      summary.baseProductKey === SOFTWARE_BASE_PRODUCT_KEYS.SW_KIOSK
    ) {
      return false;
    }
    return (
      summary.subscriptionState !== SOFTWARE_SUBSCRIPTION_STATES.NOT_SUBSCRIBED
    );
  });
};

export const fetchSubscriptionState = (product) => {
  return product.software_summary?.subscription_state || null;
};

/**
 * Fetches the trial end date for a software product
 * @param {Object} product - software product
 * @returns {String} trialEndDate - object that looks like
 * { year: 2025, month_of_year: 1, day_of_month: 1 }
 */
export const fetchTrialEndDate = (product) => {
  return product.software_summary?.free_trial_end_date || null;
};

export const isKioskOnlySoftwareInCart = (orderData) => {
  if (!orderData) return false;

  const { software } = sortProductByHardwareOrSoftware(orderData.cart_summary);

  const kioskIsInCart = software.some(
    (product) =>
      product.base_product_key === SOFTWARE_BASE_PRODUCT_KEYS.SW_KIOSK,
  );

  return kioskIsInCart && software.length === 1;
};

export const pullSoftwareFromAddOns = (addOns) => {
  const software = Object.entries(addOns).reduce((acc, [key, value]) => {
    if (key.startsWith('software_')) {
      acc.push(value);
    }

    return acc;
  }, []);

  return software.flat();
};
